(function () {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const REVEAL_SELECTOR =
    '[data-reveal], [data-reveal-immediate], [data-reveal-stagger], [data-reveal-immediate-stagger], [data-reveal-lines], [data-reveal-immediate-lines], [data-reveal-words], [data-reveal-immediate-words]';

  let ran = false;
  let running = null;

  function splitLines(el) {
    if (el.dataset.revealLinesSplit === 'true') return;

    // If el wraps a single heading/paragraph, operate on that child so we
    // don't strip it out of a flex-column wrapper (which would stack each
    // word as its own flex item — see .text-block in snippets/text.liquid).
    let target = el;
    const children = Array.from(el.children);
    if (children.length === 1 && /^(H[1-6]|P)$/i.test(children[0].tagName)) {
      target = children[0];
    }

    const text = target.textContent.trim();
    if (!text) return;

    const words = text.split(/\s+/);
    target.textContent = '';
    const wordSpans = words.map((word) => {
      const span = document.createElement('span');
      span.className = 'word';
      span.style.display = 'inline-block';
      span.textContent = word;
      target.appendChild(span);
      target.appendChild(document.createTextNode(' '));
      return span;
    });

    const lines = [];
    let currentTop = null;
    let currentLine = [];
    wordSpans.forEach((span) => {
      const top = span.offsetTop;
      if (currentTop === null || Math.abs(top - currentTop) < 2) {
        currentLine.push(span);
        currentTop = top;
      } else {
        lines.push(currentLine);
        currentLine = [span];
        currentTop = top;
      }
    });
    if (currentLine.length) lines.push(currentLine);

    target.textContent = '';
    lines.forEach((line, lineIndex) => {
      const lineEl = document.createElement('span');
      lineEl.className = 'line';
      const inner = document.createElement('span');
      inner.className = 'line__inner';
      inner.style.setProperty('--line-index', lineIndex);
      inner.textContent = line.map((s) => s.textContent).join(' ');
      lineEl.appendChild(inner);
      target.appendChild(lineEl);
    });

    el.dataset.revealLinesSplit = 'true';
  }

  /** One masked “line” per word (inline-block), same motion as reveal-lines. */
  function splitWords(el) {
    if (el.dataset.revealWordsSplit === 'true') return;

    let target = el;
    const children = Array.from(el.children);
    if (children.length === 1 && /^(H[1-6]|P)$/i.test(children[0].tagName)) {
      target = children[0];
    }

    const text = target.textContent.trim();
    if (!text) return;

    const words = text.split(/\s+/);
    target.textContent = '';
    words.forEach((word, i) => {
      const wrap = document.createElement('span');
      wrap.className = 'word-line';
      const inner = document.createElement('span');
      inner.className = 'word-line__inner';
      inner.style.setProperty('--word-index', String(i));
      inner.textContent = word;
      wrap.appendChild(inner);
      target.appendChild(wrap);
      if (i < words.length - 1) {
        target.appendChild(document.createTextNode(' '));
      }
    });

    el.dataset.revealWordsSplit = 'true';
  }

  function setStaggerIndex(el) {
    if (el.dataset.revealStaggerIndexed === 'true') return;
    Array.from(el.children).forEach((child, i) => {
      child.style.setProperty('--reveal-index', i);
    });
    el.dataset.revealStaggerIndexed = 'true';
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -10% 0px', threshold: 0.1 }
  );

  /** Split / index page-entry (immediate) reveals — still hidden until .is-revealed. */
  function prepareImmediate(root = document) {
    root.querySelectorAll('[data-reveal-immediate-stagger]:not(.is-revealed)').forEach((el) => {
      setStaggerIndex(el);
    });

    root
      .querySelectorAll(
        '[data-reveal-immediate-lines]:not([data-reveal-lines-split="true"])'
      )
      .forEach((el) => {
        if (!reducedMotion) splitLines(el);
      });

    root
      .querySelectorAll(
        '[data-reveal-immediate-words]:not([data-reveal-words-split="true"])'
      )
      .forEach((el) => {
        if (!reducedMotion) splitWords(el);
      });
  }

  /** Add .is-revealed to page-entry elements (triggers CSS transitions). */
  function revealImmediate(root = document) {
    root
      .querySelectorAll(
        '[data-reveal-immediate]:not(.is-revealed), [data-reveal-immediate-stagger]:not(.is-revealed), [data-reveal-immediate-lines]:not(.is-revealed), [data-reveal-immediate-words]:not(.is-revealed)'
      )
      .forEach((el) => {
        el.classList.add('is-revealed');
      });
  }

  /** Observe scroll-triggered reveals (excludes immediate / page-entry). */
  function scan(root = document) {
    root.querySelectorAll('[data-reveal-stagger]:not(.is-revealed)').forEach((el) => {
      setStaggerIndex(el);
      observer.observe(el);
    });

    root
      .querySelectorAll('[data-reveal-lines]:not([data-reveal-lines-split="true"])')
      .forEach((el) => {
        if (!reducedMotion) splitLines(el);
        observer.observe(el);
      });

    root
      .querySelectorAll('[data-reveal-words]:not([data-reveal-words-split="true"])')
      .forEach((el) => {
        if (!reducedMotion) splitWords(el);
        observer.observe(el);
      });

    root.querySelectorAll('[data-reveal]:not(.is-revealed)').forEach((el) => {
      observer.observe(el);
    });
  }

  function markAllRevealed(root = document) {
    root.querySelectorAll(REVEAL_SELECTOR).forEach((el) => el.classList.add('is-revealed'));
  }

  async function waitForActiveViewTransition() {
    try {
      while (document.documentElement.matches(':active-view-transition')) {
        await new Promise((resolve) => requestAnimationFrame(resolve));
      }
    } catch (_) {
      // :active-view-transition may be unsupported — ignore
    }
  }

  async function runReveals(viewTransition) {
    if (reducedMotion) {
      markAllRevealed();
      return;
    }

    if (viewTransition) {
      try {
        await viewTransition.finished;
      } catch (_) {
        // Transition skipped or aborted
      }
    } else {
      await waitForActiveViewTransition();
    }

    if (document.fonts && document.fonts.ready) {
      await document.fonts.ready;
    }

    prepareImmediate(document);

    // Double rAF so the browser applies pre-split / hidden styles before we reveal
    await new Promise((resolve) => requestAnimationFrame(resolve));
    await new Promise((resolve) => requestAnimationFrame(resolve));

    revealImmediate(document);
    scan(document);
  }

  function runOnce(viewTransition) {
    if (ran) return running;
    if (running) return running;

    running = (async () => {
      await runReveals(viewTransition);
      ran = true;
    })();

    return running;
  }

  window.addEventListener('pagereveal', (event) => {
    const { viewTransition } = /** @type {PageRevealEvent} */ (event);
    runOnce(viewTransition);
  });

  // Fallback when pagereveal never fires (older browsers) or fired before this script
  const boot = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();
  boot
    .catch(() => {})
    .then(() => {
      // Brief deferral so pagereveal can win when both are available
      return new Promise((resolve) => setTimeout(resolve, 0));
    })
    .then(() => {
      if (!ran) runOnce(null);
    });

  // Re-scan after Shopify section editor re-renders sections.
  document.addEventListener('shopify:section:load', (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (reducedMotion) {
      markAllRevealed(event.target);
      return;
    }
    prepareImmediate(event.target);
    revealImmediate(event.target);
    scan(event.target);
  });
})();
